/** Platform defaults — match server/src/utils/brand-colors.ts */
export const DEFAULT_BRAND_PRIMARY = '#0A62FF';
export const DEFAULT_BRAND_ACCENT = '#39B6FF';

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

export const normalizeHexColor = (value: string | null | undefined): string | null => {
  if (value == null) return null;
  const trimmed = value.trim();
  if (!HEX_COLOR.test(trimmed)) return null;
  return trimmed.toUpperCase();
};

const parseHex = (hex: string): [number, number, number] => {
  const value = hex.replace('#', '');
  return [
    Number.parseInt(value.slice(0, 2), 16),
    Number.parseInt(value.slice(2, 4), 16),
    Number.parseInt(value.slice(4, 6), 16),
  ];
};

const relativeLuminance = (hex: string): number => {
  const channels = parseHex(hex).map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  const [r, g, b] = channels as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrastRatio = (foreground: string, background: string): number => {
  const l1 = relativeLuminance(foreground);
  const l2 = relativeLuminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

const toHex = ([r, g, b]: [number, number, number]): string =>
  `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('').toUpperCase()}`;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  if (max === min) return [0, 0, lightness];

  const delta = max - min;
  const saturation =
    lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let hue = 0;
  if (max === red) hue = ((green - blue) / delta + (green < blue ? 6 : 0)) / 6;
  else if (max === green) hue = ((blue - red) / delta + 2) / 6;
  else hue = ((red - green) / delta + 4) / 6;
  return [hue, saturation, lightness];
};

const hueToChannel = (p: number, q: number, t: number): number => {
  let tone = t;
  if (tone < 0) tone += 1;
  if (tone > 1) tone -= 1;
  if (tone < 1 / 6) return p + (q - p) * 6 * tone;
  if (tone < 1 / 2) return q;
  if (tone < 2 / 3) return p + (q - p) * (2 / 3 - tone) * 6;
  return p;
};

const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  if (s === 0) {
    const value = Math.round(l * 255);
    return [value, value, value];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hueToChannel(p, q, h + 1 / 3) * 255),
    Math.round(hueToChannel(p, q, h) * 255),
    Math.round(hueToChannel(p, q, h - 1 / 3) * 255),
  ];
};

const shiftLightness = (hex: string, delta: number): string => {
  const [h, s, l] = rgbToHsl(...parseHex(hex));
  return toHex(hslToRgb(h, s, clamp(l + delta, 0, 1)));
};

/** Mirrors `--surface` in tokens.css — used only for contrast maths, not display. */
const SURFACE_REFERENCE = {
  light: '#FFFFFF',
  dark: '#1E293B',
} as const;

/** Mirrors `--nav-background` — the sidebar stays navy in both themes. */
const NAV_BACKGROUND = {
  light: '#0B1E3A',
  dark: '#060F1F',
} as const;

const MIN_TEXT_SURFACE_CONTRAST = 4.5;
const MIN_FILL_SURFACE_CONTRAST = 3;

/**
 * Lightens or darkens a colour only when it would fail WCAG AA against the
 * given background. Shifts lightness in HSL so the hue stays put.
 */
export const ensureReadableOnBackground = (hex: string, background: string): string => {
  if (contrastRatio(hex, background) >= MIN_TEXT_SURFACE_CONTRAST) return hex;

  const direction = relativeLuminance(background) < 0.5 ? 1 : -1;
  for (let step = 0.04; step <= 1; step += 0.04) {
    const candidate = shiftLightness(hex, direction * step);
    if (contrastRatio(candidate, background) >= MIN_TEXT_SURFACE_CONTRAST) return candidate;
  }
  return direction > 0 ? '#FFFFFF' : '#000000';
};

/**
 * Lightens or darkens a church brand colour only when it would fail WCAG AA
 * against the current theme surface. Shifts lightness in HSL so the hue stays
 * put — mixing toward white is what turned dark-mode actions slate-grey.
 */
export const ensureReadableOnSurface = (
  hex: string,
  resolvedTheme: 'light' | 'dark',
): string => ensureReadableOnBackground(hex, SURFACE_REFERENCE[resolvedTheme]);

/**
 * Sidebar selection sits on navy, not the page surface. A light gold stays
 * gold; a dark maroon is lifted so it does not disappear into the chrome.
 */
export const ensureReadableOnNavy = (
  hex: string,
  resolvedTheme: 'light' | 'dark',
): string => ensureReadableOnBackground(hex, NAV_BACKGROUND[resolvedTheme]);

/**
 * Filled actions keep a saturated mid-lightness brand in dark mode instead of
 * the pastel that text-on-surface contrast would produce. Same intent as the
 * app's `_brandButtonColor`.
 */
export const vividBrandFill = (
  hex: string,
  resolvedTheme: 'light' | 'dark',
): string => {
  if (resolvedTheme === 'light') {
    return ensureReadableOnSurface(hex, 'light');
  }

  const [h, s, l] = rgbToHsl(...parseHex(hex));
  // Dark navy eats a light-mode royal blue. Lift into the same band as the
  // platform dark token (#3B8CFF) and keep saturation high so the fill stays
  // electric instead of mixing toward slate.
  const vividS = clamp(s + 0.08, 0.72, 0.95);
  const vividL = clamp(l < 0.54 ? 0.58 : l, 0.54, 0.62);
  let candidate = toHex(hslToRgb(h, vividS, vividL));
  const surface = SURFACE_REFERENCE.dark;
  if (contrastRatio(candidate, surface) >= MIN_FILL_SURFACE_CONTRAST) return candidate;

  for (let step = 0.02; step <= 0.4; step += 0.02) {
    candidate = toHex(hslToRgb(h, vividS, clamp(vividL + step, 0, 0.72)));
    if (contrastRatio(candidate, surface) >= MIN_FILL_SURFACE_CONTRAST) return candidate;
  }
  return candidate;
};

export const contrastOnBrand = (hex: string): string =>
  relativeLuminance(hex) > 0.5 ? '#0F172A' : '#FFFFFF';

export interface ChurchBrandColors {
  brandPrimary: string;
  brandAccent: string;
}

export const applyChurchBrand = (
  colors: ChurchBrandColors,
  resolvedTheme: 'light' | 'dark',
): void => {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const sourcePrimary = normalizeHexColor(colors.brandPrimary) ?? DEFAULT_BRAND_PRIMARY;
  const accent = normalizeHexColor(colors.brandAccent) ?? DEFAULT_BRAND_ACCENT;
  const primary = vividBrandFill(sourcePrimary, resolvedTheme);
  const navAccent = ensureReadableOnNavy(sourcePrimary, resolvedTheme);

  root.style.setProperty('--brand-primary', primary);
  root.style.setProperty('--brand-accent', accent);
  root.style.setProperty('--brand-on-primary', contrastOnBrand(primary));
  root.style.setProperty(
    '--brand-subtle',
    resolvedTheme === 'dark'
      ? `color-mix(in oklch, ${primary} 18%, var(--surface))`
      : `color-mix(in oklch, ${primary} 12%, white)`,
  );
  root.style.setProperty(
    '--nav-item-active',
    `color-mix(in oklch, ${navAccent} 22%, transparent)`,
  );
  root.style.setProperty('--nav-item-active-text', navAccent);

  if (resolvedTheme === 'dark') {
    root.style.setProperty(
      '--brand-primary-hover',
      `color-mix(in oklch, ${primary} 88%, white)`,
    );
    root.style.setProperty(
      '--brand-primary-active',
      `color-mix(in oklch, ${primary} 76%, white)`,
    );
  } else {
    root.style.setProperty(
      '--brand-primary-hover',
      `color-mix(in oklch, ${primary} 88%, black)`,
    );
    root.style.setProperty(
      '--brand-primary-active',
      `color-mix(in oklch, ${primary} 76%, black)`,
    );
  }
};

export const clearChurchBrand = (): void => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const prop of [
    '--brand-primary',
    '--brand-primary-hover',
    '--brand-primary-active',
    '--brand-accent',
    '--brand-on-primary',
    '--brand-subtle',
    '--nav-item-active',
    '--nav-item-active-text',
  ]) {
    root.style.removeProperty(prop);
  }
};
